import { Entry } from '../../db/DB';
import type { AsyncGetterInvokeFn, AsyncGettersCallables } from '../../ReplicantAsyncGetters';
import { Replicant } from '../../ReplicantConfig';
import { APIAccessInterface } from '../../server/ServerReplicant';
import type { AsyncGetterServerCallableOpts } from '../../server/ServerReplicantAsyncGetters';
import { addSystemStateFields, getSystemStateFieldsFromEntry } from '../../systemStateFields';
import { deepCopyJSON } from '../../utils/ObjUtils';
import { ReportingAPI } from './createReportingAPIs';

/** Generate the `api.asyncGetters.*` async action API object. */
export function generateAsyncGettersAPI<T extends Replicant>(
    opts: {
        id: string;
        sessionId: string;
        entry: Entry<T['state'], T['sharedStates']>;
        apiAccess: APIAccessInterface<T>;
        consistentFetchIds: string[];
        invokeTime: number;
    } & ReportingAPI,
): AsyncGettersCallables<T['asyncGetters']> {
    const { apiAccess, consistentFetchIds, entry, id, invokeTime, reportError, sendAnalyticsEvents, sessionId } = opts;

    const getOwnEntry = async () => {
        const entryCopy = deepCopyJSON(entry);
        addSystemStateFields(entryCopy.state, getSystemStateFieldsFromEntry(id, entryCopy), opts.apiAccess.config);
        return entryCopy;
    };

    const clockOffset = entry.metainfo.clockOffset || 0;

    const asyncGetterOpts: AsyncGetterServerCallableOpts = {
        getOwnEntry,
        id,
        sessionId,
        clockOffset,
        consistentFetchIds,
        dateNow: () => invokeTime,
        reportError,
        sendAnalyticsEvents,
    };

    const asyncGettersAPI = {} as AsyncGettersCallables<any>;

    for (const name in apiAccess.asyncGetters.asyncGetters) {
        const asyncGetter = apiAccess.asyncGetters.asyncGetters[name];

        if (typeof asyncGetter === 'function') {
            asyncGettersAPI[name] = (args) => asyncGetter(args, asyncGetterOpts);
        } else {
            const namespace = {} as Record<string, AsyncGetterInvokeFn<any>>;

            for (const nestedName in asyncGetter) {
                namespace[nestedName] = (args) => asyncGetter[nestedName]!(args, asyncGetterOpts);
            }

            asyncGettersAPI[name] = namespace;
        }
    }

    return asyncGettersAPI;
}
